import React, { useEffect, useRef, useState } from "react";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import { useAuth } from "../context/AuthContext";
import {
  getEmployees,
  createEmployee,
  updateEmployee,
  uploadEmployeeDocumento,
  toggleEmployee,
  deleteEmployee,
  setUserPassword,
} from "../services/api";
import { UserPlus, Pencil, Trash2, Search, X, Upload, ExternalLink, Power } from "lucide-react";

const ROLE_ORDER = {
  "admin-plus": 0,
  "admin":      1,
  "lector-plus": 2,
  "field_agent": 2,
  "lector":     3,
  "administrador-whatsapp": 4,
};

const ROLE_LABEL = {
  lector: "Auditor",
  "lector-plus": "Auditor Plus",
  field_agent: "Auditor Plus",
  admin: "Admin",
  "admin-plus": "Admin Plus",
  "administrador-whatsapp": "WhatsApp",
};

const ROLE_COLORS = {
  lector:        { background: "rgba(0,122,255,0.08)",   color: "#007aff" },
  "lector-plus": { background: "rgba(147,51,234,0.08)",  color: "#7c3aed" },
  field_agent:   { background: "rgba(147,51,234,0.08)",  color: "#7c3aed" },
  admin:         { background: "rgba(212,98,13,0.09)",   color: "#d4620d" },
  "admin-plus":  { background: "rgba(194,65,12,0.09)",   color: "#c2410c" },
  "administrador-whatsapp": { background: "rgba(52,199,89,0.1)", color: "#15803d" },
};

export default function UsersPage() {
  const { user } = useAuth();
  const isAdminPlus = user?.role === "admin-plus";
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  async function load() {
    try { setEmployees(await getEmployees()); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function handleToggle(emp) {
    if (!window.confirm(`¿${emp.active ? "Desactivar" : "Activar"} a ${emp.name}?`)) return;
    try { await toggleEmployee(emp.id, !emp.active); load(); }
    catch (err) { alert(err.message); }
  }

  const filtered = employees
    .filter(emp => {
      const q = search.toLowerCase();
      return (
        emp.name.toLowerCase().includes(q) ||
        emp.email.toLowerCase().includes(q) ||
        (emp.phone || "").toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      if (a.active !== b.active) return a.active ? -1 : 1;
      const ra = ROLE_ORDER[a.role] ?? 99;
      const rb = ROLE_ORDER[b.role] ?? 99;
      return ra - rb;
    });

  return (
    <div>
      <DeleteConfirmModal
        visible={!!deleteTarget}
        message={`¿Eliminar definitivamente a ${deleteTarget?.name}? Esta acción no se puede deshacer.`}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={async () => {
          await deleteEmployee(deleteTarget.id);
          setDeleteTarget(null);
          load();
        }}
      />

      <div style={s.header}>
        <div>
          <h1 style={s.title}>Usuarios</h1>
          {!loading && (
            <p style={s.subtitle}>
              {employees.length} {employees.length === 1 ? "usuario" : "usuarios"}
            </p>
          )}
        </div>
        <div style={s.headerRight}>
          <div style={s.searchWrap}>
            <Search size={14} color="#aeaeb2" style={{ flexShrink: 0 }} />
            <input
              style={s.searchInput}
              placeholder="Buscar..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button style={s.clearBtn} onClick={() => setSearch("")}>
                <X size={12} />
              </button>
            )}
          </div>
          {isAdminPlus && (
            <button style={s.addBtn} onClick={() => setShowCreate(true)}>
              <UserPlus size={14} style={{ marginRight: 6 }} />
              Nuevo usuario
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div style={s.loading}>Cargando...</div>
      ) : filtered.length === 0 ? (
        <div style={s.empty}>
          {search ? "Sin resultados para esa búsqueda." : "No hay usuarios."}
        </div>
      ) : (
        <div style={s.listCard}>
          {filtered.map((emp, i) => {
            const roleColor = ROLE_COLORS[emp.role] || { background: "#f2f2f7", color: "#8e8e93" };
            const hoursToday = parseFloat(emp.hours_today) || 0;
            const hh = Math.floor(hoursToday);
            const mm = Math.round((hoursToday - hh) * 60);
            const hoursLabel = emp.today_readings > 0
              ? `${hh}h ${mm.toString().padStart(2,"0")}m`
              : "—";
            const firstTime = emp.first_reading_today
              ? new Date(emp.first_reading_today).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })
              : null;
            const lastTime = emp.last_reading_today
              ? new Date(emp.last_reading_today).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })
              : null;

            return (
              <div
                key={emp.id}
                style={{
                  ...s.row,
                  borderBottom: i < filtered.length - 1 ? "1px solid var(--border)" : "none",
                  opacity: emp.active ? 1 : 0.45,
                }}
              >
                <div style={s.rowLeft}>
                  {/* Dot online real (last_seen < 15 min) */}
                  <div style={{
                    width: 8, height: 8, borderRadius: "50%", flexShrink: 0, marginRight: 4,
                    background: emp.is_online ? "#34c759" : "#c7c7cc",
                  }} title={emp.is_online ? "Activo ahora" : "Inactivo"} />
                  <div style={s.avatar}>{emp.name.charAt(0).toUpperCase()}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={s.empName}>{emp.name}</div>
                    <div style={s.empEmail}>{emp.email}</div>
                  </div>
                </div>

                <span style={{ ...s.rolePill, ...roleColor }}>
                  {ROLE_LABEL[emp.role] || emp.role}
                </span>

                <span style={s.phone}>{emp.phone || <span style={{ color: "#c7c7cc" }}>—</span>}</span>

                {/* Horas conectado hoy */}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", minWidth: 90 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: hoursToday > 0 ? "var(--text)" : "var(--text-3)" }}>
                    {hoursLabel}
                  </span>
                  {firstTime && lastTime && (
                    <span style={{ fontSize: 10, color: "var(--text-3)", marginTop: 1 }}>
                      {firstTime} → {lastTime}
                    </span>
                  )}
                  {emp.today_readings > 0 && (
                    <span style={{ fontSize: 10, color: "var(--text-3)" }}>
                      {emp.today_readings} lecturas
                    </span>
                  )}
                </div>

                {isAdminPlus && (
                  <div style={s.actions}>
                    <button style={s.iconBtn} title="Editar" onClick={() => setEditTarget(emp)}>
                      <Pencil size={14} />
                    </button>
                    <button
                      style={{ ...s.iconBtn, color: emp.active ? "#ff3b30" : "#34c759" }}
                      title={emp.active ? "Desactivar" : "Activar"}
                      onClick={() => handleToggle(emp)}
                    >
                      <Power size={14} />
                    </button>
                    <button
                      style={{ ...s.iconBtn, color: "#ff3b30" }}
                      title="Eliminar"
                      onClick={() => setDeleteTarget(emp)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {isAdminPlus && showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); }}
        />
      )}
      {isAdminPlus && editTarget && (
        <EditModal
          employee={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={updated => {
            setEmployees(prev => prev.map(e => e.id === updated.id ? { ...e, ...updated } : e));
            setEditTarget(null);
          }}
        />
      )}
    </div>
  );
}

// ─── Create modal ─────────────────────────────────────────────────────────────

function CreateModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", role: "lector" });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  function update(field, value) { setForm(f => ({ ...f, [field]: value })); }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.password) {
      setError("Nombre, email y contraseña son obligatorios");
      return;
    }
    setSaving(true);
    try { await createEmployee(form); onSaved(); }
    catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  return (
    <Modal title="Nuevo usuario" onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <RoleSelector value={form.role} onChange={v => update("role", v)} />
        <Field label="Nombre completo" value={form.name} onChange={v => update("name", v)} placeholder="Juan Pérez" required />
        <Field label="Email" value={form.email} onChange={v => update("email", v)} placeholder="juan@empresa.com" type="email" required />
        <Field label="Contraseña" value={form.password} onChange={v => update("password", v)} placeholder="Mínimo 6 caracteres" type="password" required />
        <Field label="Teléfono" value={form.phone} onChange={v => update("phone", v)} placeholder="Opcional" />
        {error && <p style={s.error}>{error}</p>}
        <div style={s.formActions}>
          <button type="button" style={s.btnCancel} onClick={onClose}>Cancelar</button>
          <button type="submit" style={s.btnPrimary} disabled={saving}>
            {saving ? "Creando..." : "Crear usuario"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Edit modal ───────────────────────────────────────────────────────────────

const DOC_SLOTS = [
  { campo: "credential_front",     label: "Credencial — Frente",     key: "credential_front_url" },
  { campo: "credential_back",      label: "Credencial — Dorso",      key: "credential_back_url" },
  { campo: "seguro_laboral_front", label: "Seguro Laboral — Frente", key: "seguro_laboral_front_url" },
  { campo: "seguro_laboral_back",  label: "Seguro Laboral — Dorso",  key: "seguro_laboral_back_url" },
];

function EditModal({ employee, onClose, onSaved }) {
  const [form, setForm] = useState({ name: employee.name, phone: employee.phone || "", role: employee.role });
  const [docs, setDocs] = useState({
    credential_front_url:     employee.credential_front_url     || null,
    credential_back_url:      employee.credential_back_url      || null,
    seguro_laboral_front_url: employee.seguro_laboral_front_url || null,
    seguro_laboral_back_url:  employee.seguro_laboral_back_url  || null,
  });
  const [pendingFiles, setPendingFiles] = useState({});
  const [saving, setSaving]       = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState("");

  const [newPwd, setNewPwd]       = useState("");
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdError, setPwdError]   = useState("");
  const [pwdOk, setPwdOk]         = useState(false);

  const fileRefs = useRef({});

  function update(field, value) { setForm(f => ({ ...f, [field]: value })); }

  function onFileChange(campo, key, e) {
    const file = e.target.files[0];
    if (!file) return;
    setPendingFiles(p => ({ ...p, [campo]: file }));
    setDocs(d => ({ ...d, [key]: URL.createObjectURL(file) }));
  }

  async function handleSave() {
    setError("");
    setSaving(true);
    try {
      let updated = await updateEmployee(employee.id, form);
      for (const { campo, key } of DOC_SLOTS) {
        if (pendingFiles[campo]) {
          setUploading(true);
          const result = await uploadEmployeeDocumento(employee.id, campo, pendingFiles[campo]);
          updated = { ...updated, [key]: result.url };
        }
      }
      onSaved(updated);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
      setUploading(false);
    }
  }

  async function handleChangePwd() {
    if (!newPwd || newPwd.length < 4) { setPwdError("Mínimo 4 caracteres"); return; }
    setPwdError(""); setPwdOk(false); setPwdSaving(true);
    try {
      await setUserPassword(employee.id, newPwd);
      setPwdOk(true);
      setNewPwd("");
    } catch (err) {
      setPwdError(err.message || "Error al guardar");
    } finally {
      setPwdSaving(false);
    }
  }

  return (
    <Modal title="Editar usuario" onClose={onClose} wide>
      <div style={s.editGrid}>
        {/* Left: info + password */}
        <div>
          <RoleSelector value={form.role} onChange={v => update("role", v)} />
          <Field label="Nombre completo" value={form.name} onChange={v => update("name", v)} />
          <Field label="Teléfono" value={form.phone} onChange={v => update("phone", v)} placeholder="Opcional" />

          <div style={s.pwdSection}>
            <label style={s.fieldLabel}>Resetear contraseña</label>
            <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
              <input
                type="password"
                value={newPwd}
                onChange={e => { setNewPwd(e.target.value); setPwdOk(false); setPwdError(""); }}
                placeholder="Nueva contraseña..."
                style={{ ...s.input, flex: 1 }}
                autoComplete="new-password"
              />
              <button
                style={{ ...s.btnPrimary, padding: "10px 16px", flexShrink: 0 }}
                onClick={handleChangePwd}
                disabled={pwdSaving || !newPwd}
              >
                {pwdSaving ? "..." : "Guardar"}
              </button>
            </div>
            {pwdError && <p style={{ ...s.error, marginTop: 6 }}>{pwdError}</p>}
            {pwdOk    && <p style={{ color: "#34c759", fontSize: 13, marginTop: 6 }}>Contraseña actualizada ✓</p>}
          </div>
        </div>

        {/* Right: 4 document slots */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          {DOC_SLOTS.map(({ campo, label, key }) => (
            <div key={campo}>
              <p style={s.fieldLabel}>{label}</p>
              <div style={s.docBox} onClick={() => fileRefs.current[campo]?.click()}>
                {docs[key] ? (
                  <img src={docs[key]} alt={label} style={s.docPreview} />
                ) : (
                  <div style={s.docPlaceholder}>
                    <Upload size={18} color="#c7c7cc" />
                    <span style={{ fontSize: 12, color: "#aeaeb2", marginTop: 6 }}>Subir imagen</span>
                  </div>
                )}
              </div>
              <input
                ref={el => fileRefs.current[campo] = el}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={e => onFileChange(campo, key, e)}
              />
              {docs[key] && (
                <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
                  <button style={s.docAction} onClick={() => fileRefs.current[campo]?.click()}>
                    Cambiar
                  </button>
                  <a href={docs[key]} target="_blank" rel="noreferrer" style={{ ...s.docAction, textDecoration: "none", display: "flex", alignItems: "center", gap: 4 }}>
                    <ExternalLink size={11} /> Ver
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {error && <p style={s.error}>{error}</p>}

      <div style={s.formActions}>
        <button style={s.btnCancel} onClick={onClose}>Cancelar</button>
        <button style={s.btnPrimary} onClick={handleSave} disabled={saving || uploading}>
          {uploading ? "Subiendo..." : saving ? "Guardando..." : "Guardar cambios"}
        </button>
      </div>
    </Modal>
  );
}

// ─── Shared components ────────────────────────────────────────────────────────

function Modal({ title, onClose, children, wide }) {
  return (
    <div style={s.overlay} onClick={onClose}>
      <div
        style={{ ...s.modal, maxWidth: wide ? 700 : 500 }}
        onClick={e => e.stopPropagation()}
      >
        <div style={s.modalHeader}>
          <h2 style={s.modalTitle}>{title}</h2>
          <button style={s.closeBtn} onClick={onClose}>
            <X size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function RoleSelector({ value, onChange }) {
  const roles = [
    ["lector",                   "Auditor"],
    ["lector-plus",              "Auditor Plus"],
    ["admin",                    "Admin"],
    ["admin-plus",               "Admin Plus"],
    ["administrador-whatsapp",   "Admin WhatsApp"],
  ];
  return (
    <div style={{ marginBottom: 20 }}>
      <label style={s.fieldLabel}>Rol</label>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 2 }}>
        {roles.map(([val, label]) => (
          <button
            key={val}
            type="button"
            style={value === val ? s.roleBtnActive : s.roleBtn}
            onClick={() => onChange(val)}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", required }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={s.fieldLabel}>{label}{required && <span style={{ color: "#ff3b30", marginLeft: 2 }}>*</span>}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={s.input}
      />
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const s = {
  // Page layout
  header: {
    display: "flex", alignItems: "flex-start", justifyContent: "space-between",
    marginBottom: 28, flexWrap: "wrap", gap: 16,
  },
  title:    { fontSize: 28, fontWeight: 600, color: "var(--text)", margin: 0, lineHeight: 1.15 },
  subtitle: { fontSize: 14, color: "#aeaeb2", margin: "4px 0 0", fontWeight: 400 },
  headerRight: { display: "flex", alignItems: "center", gap: 10, flexShrink: 0 },

  // Search
  searchWrap: {
    display: "flex", alignItems: "center", gap: 8,
    background: "var(--bg-card)", border: "1px solid var(--border)",
    borderRadius: 20, padding: "7px 14px",
  },
  searchInput: {
    border: "none", outline: "none", fontSize: 14, color: "var(--text)",
    fontFamily: "Montserrat, sans-serif", width: 170, background: "transparent",
  },
  clearBtn: {
    background: "none", border: "none", cursor: "pointer",
    color: "#aeaeb2", display: "flex", alignItems: "center", padding: 2,
  },

  // Add button
  addBtn: {
    display: "flex", alignItems: "center", padding: "8px 18px",
    borderRadius: 20, border: "none", background: "#D4620D", color: "#fff",
    fontWeight: 600, cursor: "pointer", fontSize: 14,
    fontFamily: "Montserrat, sans-serif",
  },

  // States
  loading: { textAlign: "center", padding: 60, color: "#aeaeb2", fontSize: 14 },
  empty:   { textAlign: "center", padding: 60, color: "#aeaeb2", fontSize: 14 },

  // List
  listCard: {
    background: "var(--bg-card)", borderRadius: 16, overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
  },
  row: {
    display: "flex", alignItems: "center", gap: 16, padding: "14px 20px",
  },
  rowLeft: {
    display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0,
  },

  // Avatar
  avatar: {
    width: 38, height: 38, borderRadius: "50%",
    background: "rgba(212,98,13,0.1)", color: "#D4620D",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontWeight: 700, fontSize: 16, flexShrink: 0,
  },
  empName:  { fontSize: 14, fontWeight: 600, color: "var(--text)", lineHeight: 1.25 },
  empEmail: { fontSize: 12, color: "#aeaeb2", marginTop: 1 },

  // Role pill
  rolePill: {
    padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600,
    whiteSpace: "nowrap", flexShrink: 0,
  },

  // Phone
  phone: { fontSize: 13, color: "#8e8e93", minWidth: 100, flexShrink: 0 },

  // Row actions
  actions: { display: "flex", alignItems: "center", gap: 2, flexShrink: 0 },
  iconBtn: {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 32, height: 32, borderRadius: 8, border: "none",
    background: "transparent", cursor: "pointer", color: "#8e8e93",
    fontFamily: "Montserrat, sans-serif",
  },

  // Modal
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000, backdropFilter: "blur(2px)",
  },
  modal: {
    background: "var(--bg-card)", borderRadius: 20, padding: 32,
    width: "92%", maxHeight: "90vh", overflow: "auto",
    boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
  },
  modalHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    marginBottom: 28,
  },
  modalTitle: { fontSize: 20, fontWeight: 600, color: "var(--text)", margin: 0 },
  closeBtn: {
    background: "rgba(142,142,147,0.12)", border: "none", cursor: "pointer",
    color: "var(--text-2)", display: "flex", alignItems: "center", justifyContent: "center",
    width: 30, height: 30, borderRadius: "50%",
  },

  // Form
  fieldLabel: {
    display: "block", fontSize: 11, fontWeight: 600, color: "#8e8e93",
    marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.07em",
  },
  input: {
    width: "100%", padding: "11px 14px", borderRadius: 10,
    border: "1px solid var(--border)", fontSize: 14, boxSizing: "border-box",
    outline: "none", fontFamily: "Montserrat, sans-serif", color: "var(--text)",
    background: "var(--bg-card)",
  },
  error:       { color: "#ff3b30", fontSize: 13, margin: 0 },
  formActions: { display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 24 },

  btnPrimary: {
    display: "flex", alignItems: "center", padding: "10px 20px",
    borderRadius: 12, border: "none", background: "#D4620D", color: "#fff",
    fontWeight: 600, cursor: "pointer", fontSize: 14,
    fontFamily: "Montserrat, sans-serif",
  },
  btnCancel: {
    padding: "10px 20px", borderRadius: 12, border: "1px solid var(--border)",
    background: "transparent", color: "var(--text-2)", fontWeight: 600,
    cursor: "pointer", fontSize: 14, fontFamily: "Montserrat, sans-serif",
  },

  // Role selector
  roleBtn: {
    padding: "6px 14px", borderRadius: 20, border: "1px solid var(--border)",
    background: "transparent", cursor: "pointer", fontSize: 13, color: "var(--text-2)",
    fontFamily: "Montserrat, sans-serif", fontWeight: 500,
  },
  roleBtnActive: {
    padding: "6px 14px", borderRadius: 20, border: "none",
    background: "#D4620D", color: "#fff", cursor: "pointer", fontSize: 13,
    fontWeight: 600, fontFamily: "Montserrat, sans-serif",
  },

  // Edit grid
  editGrid:   { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32, marginBottom: 8 },
  pwdSection: { marginTop: 20, paddingTop: 18, borderTop: "1px solid var(--border)" },

  // Document upload
  docBox: {
    border: "1.5px dashed var(--border)", borderRadius: 12, height: 150, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
    background: "var(--bg-subtle)",
  },
  docPreview:     { width: "100%", height: "100%", objectFit: "contain" },
  docPlaceholder: { display: "flex", flexDirection: "column", alignItems: "center" },
  docAction: {
    background: "none", border: "none", color: "#D4620D", fontSize: 12,
    cursor: "pointer", fontFamily: "Montserrat, sans-serif", padding: 0,
  },
};
